<template>
    <div class="container">
        <a-spin tip="Carregando..." :spinning="spinning">
            <a-config-provider :locale="currentLocale">
                <a-card>
                    <template #title>
                        <a-breadcrumb separator=">">
                            <a-breadcrumb-item>
                                <span>Dicionário de dados</span>
                            </a-breadcrumb-item>
                            <a-breadcrumb-item>Comentários</a-breadcrumb-item>
                        </a-breadcrumb>
                    </template>

                    <br />

                    <a-alert
                        v-if="statusLoaded && !isPostgres"
                        type="warning"
                        show-icon
                        style="margin-bottom: 16px;"
                        message="Comentários via metadados PostgreSQL (COMMENT ON TABLE / COMMENT ON COLUMN)."
                        description="A conexão ativa do Laravel não é pgsql. Defina DB_CONNECTION como PostgreSQL para usar esta tela."
                    />

                    <template v-else-if="isPostgres">
                        <a-row :gutter="16" style="margin-bottom: 16px;">
                            <a-col :span="12">
                                <div style="margin-bottom: 6px; font-size: 12px; color: #666;">
                                    Tabela (schema.tabela)
                                </div>
                                <a-select
                                    v-model:value="selectedQualifiedTable"
                                    show-search
                                    :filter-option="filterTableOption"
                                    placeholder="Selecione uma tabela"
                                    style="width: 350px; max-width: 100%;"
                                    :options="tableOptions"
                                    allow-clear
                                    @change="onTableChange"
                                />
                            </a-col>
                        </a-row>

                        <template v-if="selectedQualifiedTable">
                            <a-row :gutter="16" style="margin-bottom: 16px;" align="middle">
                                <a-col :xs="24" :md="18" :lg="20">
                                    <div style="margin-bottom: 6px; font-size: 12px; color: #666;">
                                        Comentário da tabela (COMMENT ON TABLE)
                                    </div>
                                    <a-textarea
                                        v-model:value="draftTableDescription"
                                        :rows="2"
                                        placeholder="Descrição da tabela"
                                    />
                                </a-col>
                                <a-col :xs="24" :md="6" :lg="4">
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        :loading="tableCommentSaving"
                                        @click="saveTableComment"
                                    >
                                        <template #icon>
                                            <a-tooltip>
                                                <template #title>Salvar comentário da tabela</template>
                                                <SaveOutlined />
                                            </a-tooltip>
                                        </template>
                                    </a-button>
                                </a-col>
                            </a-row>
                        </template>

                        <a-table
                            :columns="columnDefs"
                            :data-source="rows"
                            :pagination="false"
                            row-key="column_name"
                            bordered
                        >
                            <template #bodyCell="{ column, record }">
                                <template v-if="column.key === 'description'">
                                    <a-textarea
                                        v-model:value="record.draftDescription"
                                        :rows="2"
                                        placeholder="Comentário (COMMENT ON COLUMN)"
                                        :disabled="!selectedQualifiedTable"
                                    />
                                </template>
                                <template v-else-if="column.key === 'acao'">
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        :loading="record.saving"
                                        :disabled="!selectedQualifiedTable"
                                        @click="saveRow(record)"
                                    >
                                        <template #icon>
                                            <a-tooltip>
                                                <template #title>Salvar comentário da coluna</template>
                                                <SaveOutlined />
                                            </a-tooltip>
                                        </template>
                                    </a-button>
                                </template>
                                <template v-else>
                                    <span>{{ record[column.dataIndex] }}</span>
                                </template>
                            </template>
                        </a-table>
                    </template>
                </a-card>
            </a-config-provider>
        </a-spin>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { notification } from 'ant-design-vue'
import { SaveOutlined } from '@ant-design/icons-vue'
import ptBR from 'ant-design-vue/es/locale/pt_BR'

const currentLocale = ref(ptBR)
const spinning = ref(true)
const statusLoaded = ref(false)
const isPostgres = ref(false)
const selectedQualifiedTable = ref(undefined)
const tables = ref([])
const draftTableDescription = ref('')
const tableCommentSaving = ref(false)

const selectedParts = computed(() => {
    const q = selectedQualifiedTable.value
    if (!q || typeof q !== 'string') {
        return null
    }
    const dot = q.indexOf('.')
    if (dot < 1 || dot === q.length - 1) {
        return null
    }
    return {
        schema_name: q.slice(0, dot),
        table_name: q.slice(dot + 1),
        qualified_name: q,
    }
})

const rows = ref([])

const columnDefs = ref([
    { title: 'Coluna', dataIndex: 'column_name', key: 'column_name', width: '18%' },
    { title: 'Tipo', dataIndex: 'data_type', key: 'data_type', width: '22%' },
    { title: 'Obrigatório', dataIndex: 'not_null_label', key: 'not_null_label', width: '10%' },
    { title: 'Comentário', dataIndex: 'description', key: 'description' },
    { title: 'Ação', key: 'acao', width: 90 },
])

const tableOptions = computed(() =>
    tables.value.map((t) => ({
        value: t.qualified_name,
        label: t.qualified_name,
    }))
)

function filterTableOption(input, option) {
    const v = (option?.value ?? option?.label ?? '').toString().toLowerCase()
    return v.includes((input ?? '').toLowerCase())
}

function openNotification(type, message) {
    notification[type]({
        message: 'Notificação',
        description: message,
    })
}

async function loadStatus() {
    const { data } = await window.axios.get('api/configurations/data-dictionary/status')
    isPostgres.value = !!data.data?.postgres
    statusLoaded.value = true
}

async function loadTables() {
    const { data } = await window.axios.get('api/configurations/data-dictionary/tables')
    if (data.error) {
        openNotification('error', data.message || 'Não foi possível listar tabelas.')
        tables.value = []
        return
    }
    tables.value = data.data || []
}

async function loadColumns() {
    const parts = selectedParts.value
    if (!parts) {
        rows.value = []
        draftTableDescription.value = ''
        return
    }
    spinning.value = true
    try {
        const { data } = await window.axios.get('api/configurations/data-dictionary/columns', {
            params: {
                schema_name: parts.schema_name,
                table_name: parts.table_name,
            },
        })
        if (data.error) {
            openNotification('error', data.message || 'Erro ao carregar colunas.')
            rows.value = []
            draftTableDescription.value = ''
            return
        }
        const payload = data.data || {}
        const cols = payload.columns || []
        draftTableDescription.value = payload.table_description ?? ''
        rows.value = cols.map((c) => ({
            ...c,
            not_null_label: c.not_null ? 'Sim' : 'Não',
            draftDescription: c.description ?? '',
            saving: false,
        }))
    } finally {
        spinning.value = false
    }
}

function onTableChange() {
    loadColumns()
}

async function saveTableComment() {
    const parts = selectedParts.value
    if (!parts) {
        return
    }
    tableCommentSaving.value = true
    try {
        const { data } = await window.axios.put('api/configurations/data-dictionary/table-comment', {
            schema_name: parts.schema_name,
            table_name: parts.table_name,
            comment: draftTableDescription.value ?? '',
        })
        if (data.error) {
            openNotification('error', data.message || 'Erro ao salvar.')
            return
        }
        openNotification('success', data.message || 'Comentário da tabela gravado no PostgreSQL.')
    } catch (e) {
        const msg =
            e.response?.data?.message ||
            e.message ||
            'Erro ao salvar comentário da tabela.'
        openNotification('error', msg)
    } finally {
        tableCommentSaving.value = false
    }
}

async function saveRow(record) {
    const parts = selectedParts.value
    if (!parts) {
        return
    }
    record.saving = true
    try {
        const payload = {
            schema_name: parts.schema_name,
            table_name: parts.table_name,
            column_name: record.column_name,
            comment: record.draftDescription ?? '',
        }
        const { data } = await window.axios.put(
            'api/configurations/data-dictionary/column-comment',
            payload
        )
        if (data.error) {
            openNotification('error', data.message || 'Erro ao salvar.')
            return
        }
        record.description = payload.comment || null
        openNotification('success', data.message || 'Comentário da coluna gravado no PostgreSQL.')
    } catch (e) {
        const msg =
            e.response?.data?.message ||
            e.message ||
            'Erro ao salvar comentário.'
        openNotification('error', msg)
    } finally {
        record.saving = false
    }
}

onMounted(async () => {
    spinning.value = true
    try {
        await loadStatus()
        if (isPostgres.value) {
            await loadTables()
        }
    } catch (e) {
        openNotification('error', e.response?.data?.message || e.message || 'Erro ao carregar.')
    } finally {
        spinning.value = false
    }
})
</script>

<style scoped>
.anticon {
    vertical-align: 2px !important;
}
</style>
